/**
 * « Mes appels non classés » — bloc de la saisie quotidienne (règle William du 02/10/2026).
 *
 * Un appel décroché d'au moins une minute qui n'est ni rattaché à un ticket ni marqué « Traité »
 * n'est compté nulle part : son temps manque au ticket, et la facture du client est REFUSÉE tant
 * qu'il en reste (POST /api/billing/generate-invoice → 409 appels_non_classes).
 *
 * Depuis le 05/10/2026, la journée ne se VALIDE plus tant qu'il reste un appel non classé décroché
 * sur ses postes ce jour-là (POST /api/team/daily-log/validate → 400). Le bloc ne bloque pas la
 * saisie ; il permet de classer sur place, y compris pendant le verrou de 13 h : ses routes sont
 * toutes sous /api/team/daily-log/ (DAILY_LOG_LOCK_ALLOWLIST), jamais sous /api/incoming-call/.
 * - « Rattacher » (n° de ticket saisi, bouton ou Entrée) rattache l'appel à ce ticket
 *   (…/{call_id}/rattacher) ;
 * - « Pas un ticket » le marque « Traité » (…/{call_id}/pas-un-ticket : idempotent, jamais une
 *   bascule) ;
 * - « Classer » ouvre l'appel dans l'historique (créer un ticket, déplacer…) — hors verrou : rendu
 *   depuis l'écran de blocage (`depuisLeBlocage`), ce lien et « … et N autre(s) » sont masqués
 *   (l'historique s'ouvrirait SOUS l'écran, rien de visible).
 * Les deux routes ne touchent qu'un appel des postes de l'appelant ; un refus rend `detail`
 * (HTTPException) ou `error` (ticket non modifiable), affiché tel quel. Chaque résultat est AUSSI
 * écrit dans le bloc : l'écran de blocage (z-index 100000) recouvre les toasts (z-index 9999).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { PhoneCall, Check, ExternalLink } from 'lucide-react';
import { apiFetch, readApiJson } from '../lib/api';
import { useToast } from './TechComponents';

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const BTN = {
  display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600,
  padding: '3px 8px', borderRadius: 5, cursor: 'pointer', textDecoration: 'none', whiteSpace: 'nowrap',
};

// readApiJson ne lève pas sur un 4xx JSON : il rend le corps ({detail} ou {ok:false, error}).
const messageRefus = (res, defaut) => {
  const msg = res && (res.detail || res.error);
  return typeof msg === 'string' && msg ? msg : defaut;
};

export default function AppelsNonClassesBloc({ depuisLeBlocage = false }) {
  const toast = useToast();
  const [etat, setEtat] = useState({ charge: false, total: 0, appels: [], postes: [], erreur: false });
  const [enCours, setEnCours] = useState(null);
  const [numeros, setNumeros] = useState({});
  // Dernier résultat, écrit dans le bloc ({ ok, texte }) en plus du toast.
  const [message, setMessage] = useState(null);
  const reussi = (texte) => { toast.success(texte); setMessage({ ok: true, texte }); };
  const echoue = (texte) => { toast.error(texte); setMessage({ ok: false, texte }); };

  const charger = useCallback(async () => {
    try {
      const res = await apiFetch('/api/team/daily-log/mes-appels-non-classes').then(readApiJson);
      if (res && res.ok) {
        setEtat({ charge: true, total: res.total || 0, appels: res.appels || [], postes: res.postes || [], erreur: false });
      } else {
        setEtat((e) => ({ ...e, charge: true, erreur: true }));
      }
    } catch {
      setEtat((e) => ({ ...e, charge: true, erreur: true }));
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const pasUnTicket = async (callId) => {
    setEnCours(callId);
    setMessage(null);
    try {
      const res = await apiFetch(
        `/api/team/daily-log/mes-appels-non-classes/${encodeURIComponent(callId)}/pas-un-ticket`,
        { method: 'POST' },
      ).then(readApiJson);
      if (res && res.ok) {
        reussi('Appel marqué « Traité » (pas un ticket)');
        await charger();
      } else {
        echoue(messageRefus(res, 'Classement impossible'));
      }
    } catch (e) {
      echoue(e.message || 'Classement impossible');
    }
    setEnCours(null);
  };

  const rattacher = async (callId) => {
    if (enCours === callId) return;   // Entrée pendant l'envoi : le bouton est grisé, pas le champ
    const numero = (numeros[callId] || '').trim();
    if (!numero) { echoue('Indique le numéro du ticket'); return; }
    setEnCours(callId);
    setMessage(null);
    try {
      const res = await apiFetch(
        `/api/team/daily-log/mes-appels-non-classes/${encodeURIComponent(callId)}/rattacher`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ numero }) },
      ).then(readApiJson);
      if (res && res.ok) {
        reussi(`Appel rattaché au ticket #${res.ticket_number}`);
        await charger();
      } else {
        echoue(messageRefus(res, 'Rattachement impossible'));
      }
    } catch (e) {
      echoue(e.message || 'Rattachement impossible');
    }
    setEnCours(null);
  };

  // Ligne du dernier résultat : role="alert" pour un refus, "status" pour une réussite.
  const ligneMessage = message && (
    <div role={message.ok ? 'status' : 'alert'}
      style={{ marginTop: 6, fontSize: 12, fontWeight: 600, color: message.ok ? '#166534' : '#b91c1c' }}>
      {message.texte}
    </div>
  );

  if (!etat.charge) return null;
  // Compte sans poste téléphonique : rien à classer, rien à afficher.
  if (!etat.erreur && etat.postes.length === 0) return null;

  const cadre = {
    marginBottom: 12, padding: '10px 12px', borderRadius: 8, background: '#fff',
    border: `1px solid ${etat.total > 0 ? '#fca5a5' : 'var(--border-light)'}`,
  };

  if (etat.erreur) {
    return (
      <div style={cadre} data-testid="appels-non-classes">
        <span style={{ fontSize: 12, color: '#b45309' }}>
          Tes appels non classés n'ont pas pu être lus. Vérifie-les dans l'historique des appels.
        </span>
      </div>
    );
  }

  if (etat.total === 0) {
    return (
      <div style={cadre} data-testid="appels-non-classes">
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Check size={14} color="#16a34a" />
          <span style={{ fontSize: 12, color: '#166534' }}>Tous tes appels sont classés.</span>
        </div>
        {ligneMessage}
      </div>
    );
  }

  return (
    <div style={cadre} data-testid="appels-non-classes">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <PhoneCall size={14} color="#dc2626" />
        <strong style={{ fontSize: 13, color: '#991b1b' }}>
          {etat.total} appel{etat.total > 1 ? 's' : ''} non classé{etat.total > 1 ? 's' : ''}
        </strong>
        <span style={{ fontSize: 11, color: '#64748b' }}>
          Non compté{etat.total > 1 ? 's' : ''} dans un ticket : rattache-le{etat.total > 1 ? 's' : ''} à un ticket ou marque « Pas un ticket » :
          une journée ne peut être validée que lorsque ses appels sont classés. La facture du client attend aussi.
        </span>
      </div>
      <div style={{ display: 'grid', gap: 4 }}>
        {etat.appels.map((a) => (
          <div key={a.call_id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#475569', minWidth: 92 }}>{fmt(a.created_at)}</span>
            <span style={{ fontWeight: 600, color: '#1e293b', minWidth: 0 }}>{a.interlocuteur}</span>
            {a.societe && <span style={{ color: '#64748b' }}>{a.societe}</span>}
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#475569' }}>{a.minutes} min</span>
            <span style={{ flex: 1 }} />
            <input placeholder="n° ticket" aria-label={`Numéro du ticket pour l'appel de ${a.interlocuteur || 'cet interlocuteur'}`}
              value={numeros[a.call_id] || ''}
              onChange={(e) => { const v = e.target.value; setNumeros((n) => ({ ...n, [a.call_id]: v })); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); rattacher(a.call_id); } }}
              style={{ width: 84, fontSize: 11, padding: '2px 6px', border: '1px solid #cbd5e1', borderRadius: 5 }} />
            <button type="button" onClick={() => rattacher(a.call_id)} disabled={enCours === a.call_id}
              style={{ ...BTN, background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe' }}>
              Rattacher
            </button>
            {!depuisLeBlocage && (
              <Link to={`/call-history?call_id=${encodeURIComponent(a.call_id)}`}
                style={{ ...BTN, background: '#eef2ff', color: '#4338ca', border: '1px solid #c7d2fe' }}>
                <ExternalLink size={11} /> Classer
              </Link>
            )}
            <button type="button" onClick={() => pasUnTicket(a.call_id)} disabled={enCours === a.call_id}
              style={{ ...BTN, background: '#f8fafc', color: '#334155', border: '1px solid #cbd5e1' }}>
              Pas un ticket
            </button>
          </div>
        ))}
        {!depuisLeBlocage && etat.total > etat.appels.length && (
          // La vue « Non classés — mes postes » de l'historique : la même liste, toutes dates. L'historique
          // par défaut (7 jours, 500 appels au plus) ne montrait pas les plus anciens (09/10/2026).
          <Link to="/call-history?non_classes=1" style={{ fontSize: 11, color: '#4338ca' }}>
            … et {etat.total - etat.appels.length} autre(s) dans l'historique des appels
          </Link>
        )}
      </div>
      {ligneMessage}
    </div>
  );
}
